define('viewModels/privileged/queues/dataControllers/QueueAnalyticsDataController',[
    'services/analytics/queueAnalyticsService',

    'viewModels/ViewModel',
    'viewModels/privileged/analytics/dataControllers/AggregateAnalyticsLiveDataController'
], function (
    queueAnalyticsService,

    ViewModel,
    AggregateAnalyticsLiveDataController
) {
    'use strict';

    // ===================================================================================
    // Class Definition
    // ===================================================================================

    /**
     * A data controller class that provides a core set of methods to load analytics data
     * model objects for collections of Queues and return objects that to be used in views
     * for the end user. Instances of this class will manage the configuration of live
     * data subscriptions for any requested queues and the clean up the subscriptions when
     * the lifecycle of an instance ends.
     *
     * @return {QueueAnalyticsDataController}
     */
    var QueueAnalyticsDataController = ViewModel.extend(function () {
        //
        // Private Variables
        //

        var self = this;

        var analyticsDataController = self.subView(
            AggregateAnalyticsLiveDataController,
            queueAnalyticsService.loadQueuesData,
            function (id) {
                return "v2.analytics.queues." + id + ".observations";
            }
        );

        var analyticsObservationsController = self.subView(
            AggregateAnalyticsLiveDataController,
            queueAnalyticsService.loadQueuesObservations,
            function (id) {
                return ["v2.analytics.queues." + id + ".observations"];
            }
        );

        //
        // Public Methods
        //

        self.mediaType = analyticsDataController.mediaType;

        /**
         * Load a live collection of AnalyticsData objects for the provided collection of
         * Queue IDs. This method will handle subscribing the provided IDs to receive stat
         * notifications from the backend.
         *
         * @param  {String[]} idCollection - The collection of ids to request.
         * @param  {String} interval - The ISO 8601 interval to request data for.
         * @param  {Function} onNewDataHandler - A function that will be executed each time
         *  a new AnalyticsData object is created from an incoming notification.
         * @param  {Object} [opts=undefined] - An object containing optional arguments to
         *  send alongside the request to load the analytics data from the backend.
         *
         * @return {Promise} A promise object that will resolve to contain an array of
         *  AnalyticsData objects that were loaded for the provided arguments. This
         *  collection may not contain data for all requested IDs, it will only contain
         *  AnalyticsData instances for IDs that had data recorded for them. In lieu of
         *  this, when a notification is received for an AnalyticsData object that does
         *  not exist, the function passed in as the "onNewDataHandler" will be called
         *  with the new live instance.
         */
        self.getLiveAnalyticsDataForQueues = function (queueIds, interval, onNewDataHandler, opts) {
            return analyticsDataController.getLiveAnalyticsData(queueIds, interval, onNewDataHandler, opts);
        };

        self.getLiveAnalyticsObservationsForQueues = function (queueIds, interval, onNewDataHandler, opts) {
            if (opts && opts.mediaType) {
                analyticsObservationsController.mediaType(opts.mediaType);
            }
            return analyticsObservationsController.getLiveAnalyticsObservations(queueIds, interval, onNewDataHandler, opts);
        };

        /**
         * Load a static collection of AnalyticsData objects for the provided collection of
         * queue IDs. The requested IDs will not receive live notifications when updates occur.
         *
         * @param  {String[]} idCollection - The collection of ids to request.
         * @param  {String} interval - The ISO 8601 interval to request data for.
         * @param  {Object} [opts=undefined] - An object containing optional arguments to
         *  send alongside the request to load the analytics data from the backend.
         *
         * @return {Promise} A promise object that will resolve to contain an array of
         *  AnalyticsData objects that were loaded for the provided arguments. This
         *  collection may not contain data for all requested IDs, it will only contain
         *  AnalyticsData instances for IDs that had data recorded for them.
         */
        self.getStaticAnalyticsDataForQueues = function (queueIds, interval, opts) {
            return analyticsDataController.getStaticAnalyticsData(queueIds, interval, opts);
        };

        /**
         * Given a collection of IDs that refer to a queue, unsubscribe each ID from live
         * data notifications.
         *
         * @param  {String[]} queueIds An array of string IDs to unsubscribe from live
         *  data
         * @return {Promise} A promise that will resolve when the request to unsubscribe
         *  from live data updates has completed. No data will be passed to the handlers
         *  of this promise.
         */
        self.unsubscribeQueuesFromLiveData = function (queueIds) {
            return analyticsDataController.unsubscribeIdsFromLiveData(queueIds);
        };
    });

    return QueueAnalyticsDataController;
});

